import { useMemo, useState } from 'react'
import { Button, Dialog, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField, useTheme } from '@mui/material'
import CloseModalButton from '../../../../components/CloseModalButton'
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from '../../../../components/Dialog.styled'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { AddressTaskForm, TaskLog, TaskLogPayload } from '../types'
import { useAppDispatch } from '../../../../redux/store'
import * as service from '../service'
import { getPracticeTasks } from '../slice'
import useCustomerNumber from '../../../../hooks/useCustomerNumber'
import { TaskLogStatus } from '../enums'
import { addError, addSuccess } from '../../../../redux/alerts'

interface AddressTaskModalProps {
  open: boolean
  onClose: () => void
  taskLog: TaskLog
}

const initialFormData = {
  addressed_on: null,
  note: '',
  status: TaskLogStatus.Missed
}

const AddressTaskModal = ({ open, onClose, taskLog }: AddressTaskModalProps) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  
  const [formData, setFormData] = useState<AddressTaskForm>(initialFormData)
  
  const customerNumber = useCustomerNumber()

  const handleClose = () => {
    onClose()
  }

  const handleFormChange = (field: string, value: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value
    }))
  }

  const handleSubmit = async () => {
    try {
      if (formData.addressed_on) {
        const payload: TaskLogPayload = {
          id: taskLog.id,
          status: formData.status,
          userId: taskLog.userId,
          taskId: taskLog.taskId,
          date: taskLog.date,
          reminder_attempts: taskLog.reminder_attempts,
          note: formData.note || null,
          addressed_on: formData.addressed_on.toISOString(),
        }

        await service.updateTaskLog(customerNumber, payload)
        await dispatch(getPracticeTasks(customerNumber))

        dispatch(addSuccess('Your task has successfully been addressed.'))
      }
    } catch (error) {
      dispatch(addError('Failed to address task. Please try again.'))
    } finally {
      handleClose()
    }
  }

  const minDate = useMemo(() => {
    if (taskLog && taskLog.date) {
      const dateObj = dayjs(taskLog.date)
      return dateObj.isValid() ? dateObj.startOf('day') : undefined
    }
    return undefined
  }, [taskLog])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      sx={{ color: theme.palette.color50555b.main }}
      fullWidth
    >
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>Address Missed Task</StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en"
            >
              <DatePicker
                label='Addressed On'
                onChange={(newDate) => {
                  if (newDate) {
                    handleFormChange('addressed_on', newDate)
                  }
                }}
                format="MM-DD-YYYY"
                openTo="month"
                views={['year', 'month', 'day']}
                minDate={minDate}
                value={formData.addressed_on}
                slotProps={{
                  textField: { size: 'small' }
                }}
              />
            </LocalizationProvider>
          </FormControl>
          <FormControl fullWidth size="small">
            <TextField
              size="small"
              label="Corrective Action"
              value={formData.note}
              multiline
              rows={4}
              onChange={(e) => handleFormChange('note', e.target.value)}
              fullWidth
            />
          </FormControl>
          <FormControl>
            <FormLabel id="status-label">Status</FormLabel>
            <RadioGroup
              aria-labelledby="status-label"
              defaultValue="All"
              name="status"
              onChange={(e) => handleFormChange('status', e.target.value)}
            >
              <FormControlLabel value={'complete'} control={<Radio checked={formData.status === 'complete'} />} label={'Complete'} />
              <FormControlLabel value={'missed'} control={<Radio checked={formData.status === 'missed'} />} label={'Missed'} />
            </RadioGroup>
          </FormControl>
        </Stack>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button disabled={!formData.addressed_on} variant="contained" onClick={handleSubmit}>
          Ok
        </Button>
      </StyledDialogFooter>
    </Dialog>
  )
}
export default AddressTaskModal